import { useState } from "react";
import Card from "@/components/base/Card";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import { EmptyState } from "@/components/base/StateViews";
import { useToast } from "@/components/base/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { deleteCharge, updateCharge } from "@/services/chargeService";
import { annualOf, chargeCoversCycle, chargeHasAnyCycle, chargeMonthlyFor, chargeTotalMonthly } from "@/utils/charges";
import NumberInput from "@/components/base/NumberInput";
import { formatMoney } from "@/utils/format";
import { usePager } from "@/hooks/usePager";
import Pagination from "@/components/base/Pagination";
import type { Charge, ChargeCategory, Cycle } from "@/types";
import ChargeFormModal from "./ChargeFormModal";

interface Props {
  charges: Charge[];
  categories: ChargeCategory[];
  cycles: Cycle[];
  onChanged: () => void;
}

export default function ChargesPanel({ charges, categories, cycles, onChanged }: Props) {
  const { notify } = useToast();
  const { can } = useAuth();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Charge | null>(null);
  const pager = usePager();
  const [toDelete, setToDelete] = useState<Charge | null>(null);
  /** Saisie des budgets : montant mensuel modifié, par charge puis par cycle (texte brut du champ). */
  const [drafts, setDrafts] = useState<Record<string, Record<string, string>> | null>(null);
  const [savingBudgets, setSavingBudgets] = useState(false);
  const budgetMode = drafts !== null;

  const cycleIds = cycles.map((c) => c.id);
  const catName = (id: string) => categories.find((c) => c.id === id)?.name ?? "—";
  const catBadgeTone = (id: string) => {
    const idx = categories.findIndex((c) => c.id === id);
    if (idx === 1) return "primary" as const;
    if (idx === 2) return "secondary" as const;
    if (idx === 3) return "accent" as const;
    return "neutral" as const;
  };

  // Toutes les charges des cycles visibles, y compris celles dont le budget est encore à 0.
  const scoped = charges.filter((c) => chargeHasAnyCycle(c, cycleIds));
  const page = pager.slice(scoped);
  const rows = budgetMode ? scoped : page.items;
  /** Montant mensuel affiché : celui en cours de saisie, sinon celui enregistré. */
  const monthlyOf = (charge: Charge, cycleId: string) => {
    const draft = drafts?.[charge.id]?.[cycleId];
    return draft !== undefined ? Number(draft) || 0 : chargeMonthlyFor(charge, cycleId);
  };
  const monthlyTotalOf = (charge: Charge) => cycleIds.reduce((sum, id) => sum + monthlyOf(charge, id), 0);
  const totalAnnual = scoped.reduce((sum, c) => sum + annualOf(monthlyTotalOf(c)), 0);
  const withoutBudget = scoped.filter((c) => chargeTotalMonthly(c, cycleIds) === 0).length;
  const changed = scoped.filter((c) =>
    cycleIds.some((id) => drafts?.[c.id]?.[id] !== undefined && (Number(drafts[c.id][id]) || 0) !== chargeMonthlyFor(c, id)),
  );

  const setDraft = (chargeId: string, cycleId: string, value: string) =>
    setDrafts((prev) => ({ ...(prev ?? {}), [chargeId]: { ...(prev?.[chargeId] ?? {}), [cycleId]: value } }));

  const saveBudgets = async () => {
    setSavingBudgets(true);
    try {
      for (const charge of changed) {
        // Le serveur remplace tous les budgets de la charge : on envoie chaque cycle qu'elle couvre.
        await updateCharge(charge.id, {
          amounts: Object.keys(charge.cycleCategoryIds).map((cycleId) => ({
            cycleId,
            monthly: cycleIds.includes(cycleId) ? monthlyOf(charge, cycleId) : chargeMonthlyFor(charge, cycleId),
          })),
        });
      }
      notify(`Montants prévisionnels enregistrés (${changed.length} charge${changed.length > 1 ? "s" : ""}).`);
      setDrafts(null);
      onChanged();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Enregistrement impossible.", "error");
    } finally {
      setSavingBudgets(false);
    }
  };

  const openForm = (charge: Charge | null) => {
    setEditing(charge);
    setFormOpen(true);
  };

  const toggleActive = async (charge: Charge) => {
    await updateCharge(charge.id, { isActive: !charge.isActive });
    onChanged();
    notify(charge.isActive ? "Charge désactivée." : "Charge activée.", "info");
  };

  return (
    <Card padded={false}>
      <div className="flex flex-col gap-3 border-b border-background-200 p-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-start gap-3">
          <span className="w-9 h-9 rounded-md bg-primary-50 text-primary-700 flex items-center justify-center shrink-0">
            <i className="ri-stack-line text-lg"></i>
          </span>
          <div>
            <h3 className="text-base font-semibold font-heading text-foreground-950">
              Charges prévisionnelles ({scoped.length})
            </h3>
            <p className="text-xs text-foreground-500 mt-0.5">
              Budget annuel : <span className="text-foreground-800 font-medium">{formatMoney(totalAnnual)}</span>
              {withoutBudget > 0 && !budgetMode && (
                <span className="text-accent-700"> · {withoutBudget} charge{withoutBudget > 1 ? "s" : ""} sans montant prévisionnel</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {budgetMode ? (
            <>
              <Button variant="outline" onClick={() => setDrafts(null)} disabled={savingBudgets}>
                Annuler
              </Button>
              <Button icon="ri-save-3-line" onClick={saveBudgets} disabled={savingBudgets || changed.length === 0}>
                {savingBudgets ? "Enregistrement..." : `Enregistrer les montants (${changed.length})`}
              </Button>
            </>
          ) : (
            <>
              {can("charges:update") && scoped.length > 0 && (
                <Button variant="outline" icon="ri-edit-box-line" onClick={() => setDrafts({})}>
                  Saisir les montants prévisionnels
                </Button>
              )}
              <Button icon="ri-add-line" onClick={() => openForm(null)} style={can("charges:create") ? undefined : { display: "none" }}>
                Ajouter une charge
              </Button>
            </>
          )}
        </div>
      </div>
      {budgetMode && (
        <p className="border-b border-background-200 bg-primary-50/60 px-4 py-2.5 text-xs text-primary-800">
          Saisissez le montant prévu <strong>par mois</strong> pour chaque charge et chaque cycle, puis enregistrez. L'annuel se
          calcule tout seul (× 12). Un champ vide ou à 0 signifie « pas de budget ».
        </p>
      )}

      {scoped.length === 0 ? (
        <div className="p-4">
          <EmptyState
            icon="ri-stack-line"
            title="Aucune charge prévisionnelle"
            message="Ajoutez des charges (loyer, EDG, SEG, salaires...) pour construire votre budget prévisionnel."
            action={
              <Button icon="ri-add-line" onClick={() => openForm(null)} style={can("charges:create") ? undefined : { display: "none" }}>
                Ajouter une charge
              </Button>
            }
          />
        </div>
      ) : (
        <>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="border-b border-background-200 text-left text-xs uppercase tracking-wide text-foreground-500">
                <th className="px-4 py-3 font-medium">Charge</th>
                <th className="px-4 py-3 font-medium">Catégorie</th>
                {cycles.map((c) => (
                  <th key={c.id} className="px-4 py-3 font-medium text-right">
                    Mensuel {c.label}
                  </th>
                ))}
                <th className="px-4 py-3 font-medium text-right">Annuel</th>
                <th className="px-4 py-3 font-medium text-center">Statut</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((charge) => (
                <tr key={charge.id} className="border-b border-background-200 last:border-0 hover:bg-background-100/60 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <span className="w-8 h-8 rounded-md bg-background-100 border border-background-200 text-foreground-600 flex items-center justify-center shrink-0">
                        <i className={`${charge.icon} text-sm`}></i>
                      </span>
                      <span className="font-medium text-foreground-900">{charge.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={catBadgeTone(charge.categoryId)}>{catName(charge.categoryId)}</Badge>
                  </td>
                  {cycles.map((c) => (
                    <td key={c.id} className="px-4 py-3 text-right tabular-nums text-foreground-700 whitespace-nowrap">
                      {budgetMode && chargeCoversCycle(charge, c.id) ? (
                        <NumberInput
                          min={0}
                          value={drafts?.[charge.id]?.[c.id] ?? String(chargeMonthlyFor(charge, c.id) || "")}
                          onChange={(e) => setDraft(charge.id, c.id, e.target.value)}
                          placeholder="0"
                          aria-label={`Montant mensuel — ${charge.name} — ${c.label}`}
                          className="w-36 rounded-md border border-background-300 bg-background-50 px-2 py-1.5 text-right text-sm tabular-nums focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                        />
                      ) : monthlyOf(charge, c.id) > 0 ? (
                        formatMoney(monthlyOf(charge, c.id))
                      ) : (
                        "—"
                      )}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right tabular-nums font-medium text-foreground-900 whitespace-nowrap">
                    {monthlyTotalOf(charge) > 0 ? (
                      formatMoney(annualOf(monthlyTotalOf(charge)))
                    ) : (
                      <span className="text-xs font-normal text-accent-700">Montant à saisir</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      type="button"
                      onClick={() => toggleActive(charge)}
                      disabled={!can("charges:update")}
                      className="cursor-pointer"
                      aria-label={charge.isActive ? "Désactiver la charge" : "Activer la charge"}
                    >
                      <Badge tone={charge.isActive ? "success" : "neutral"} icon={charge.isActive ? "ri-check-line" : "ri-close-line"}>
                        {charge.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openForm(charge)}
                        className="press w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-200 transition-colors cursor-pointer"
                        aria-label="Modifier la charge"
                        style={can("charges:update") ? undefined : { display: "none" }}
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                      <button
                        onClick={() => setToDelete(charge)}
                        className="press w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-md text-foreground-400 hover:bg-accent-50 hover:text-accent-600 transition-colors cursor-pointer"
                        aria-label="Supprimer la charge"
                        style={can("charges:delete") ? undefined : { display: "none" }}
                      >
                        <i className="ri-delete-bin-6-line"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!budgetMode && <Pagination pager={page} className="border-t border-background-200" />}
        </>
      )}

      <ChargeFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={onChanged}
        editing={editing}
        categories={categories}
        cycles={cycles}
      />

      <ConfirmDialog
        open={toDelete !== null}
        title="Supprimer la charge"
        message={`Supprimer « ${toDelete?.name} » du budget prévisionnel ?`}
        confirmLabel="Supprimer"
        onClose={() => setToDelete(null)}
        onConfirm={async () => {
          if (toDelete) {
            await deleteCharge(toDelete.id);
            onChanged();
            notify("Charge supprimée.", "info");
          }
        }}
      />
    </Card>
  );
}